import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CinematicBackground from '../components/CinematicBackground';

function formatDetail(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || String(e)).join(' ');
  return detail?.msg || String(detail);
}

export default function ResetPassword() {
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => { if (!token) setTokenMissing(true); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (pw !== pw2) {
      toast.error('Passwords don\'t match');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      toast.success('Password reset — you are now signed in');
      await checkAuth();
      setDone(true);
      setTimeout(() => nav('/dashboard', { replace: true }), 1200);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#040A08] text-foreground overflow-hidden" data-testid="reset-root">
      <CinematicBackground intensity="dim" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.button
          onClick={() => nav('/')}
          data-testid="btn-reset-back"
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="absolute top-6 left-6 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full max-w-sm"
        >
          {tokenMissing ? (
            <div className="text-center p-8 rounded-3xl glass border border-white/10" data-testid="reset-invalid">
              <h2 className="font-serif text-2xl">Invalid reset link</h2>
              <p className="mt-3 text-xs text-muted-foreground">
                The link is missing a token. Please request a new one.
              </p>
              <button
                onClick={() => nav('/forgot-password')}
                data-testid="reset-back-to-forgot"
                className="mt-6 px-5 py-2 rounded-full bg-primary text-white text-xs uppercase tracking-widest"
              >Request new link</button>
            </div>
          ) : done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="text-center p-8 rounded-3xl glass border border-white/10"
              data-testid="reset-success"
            >
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" strokeWidth={1.4} />
              <h2 className="mt-4 font-serif text-2xl">All set</h2>
              <p className="mt-2 text-xs text-muted-foreground">Signing you in…</p>
            </motion.div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.3em] text-primary-light">
                  <Lock className="w-3 h-3" /> New Password
                </div>
                <h1 className="mt-4 font-serif text-4xl leading-tight">Set a new password</h1>
                <p className="mt-2 text-xs text-muted-foreground">
                  Choose a password with at least 8 characters — letters and numbers.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-3 p-6 rounded-3xl glass border border-white/10" data-testid="reset-form">
                <input
                  data-testid="reset-input-password"
                  type="password" autoComplete="new-password" required minLength={8}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  placeholder="New password"
                  className="w-full px-4 py-3 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-primary-light/60"
                />
                <input
                  data-testid="reset-input-confirm"
                  type="password" autoComplete="new-password" required minLength={8}
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full px-4 py-3 rounded-full bg-black/30 border border-white/10 text-sm outline-none focus:border-primary-light/60"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="reset-submit"
                  className="w-full py-3 rounded-full bg-primary text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:brightness-110"
                >
                  {busy ? 'Updating…' : 'Reset password'}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
